"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/ImageUpload";
import { FileService } from "@/services/FileService";
import { FilePurpose } from "@/interfaces/files";
import { useState, useEffect, useRef } from "react";
import { ImageIcon, Search, X, Percent } from "lucide-react";
import { ProductsService } from "@/services/ProductsService";
import type { OfferProducts, OffersOutput } from "@/interfaces/offers";
import type { ProductsOutput } from "@/interfaces";
import { numberToMoney } from "@/util/moneyConversion";

const productsService = new ProductsService();
const fileService = new FileService();

interface OfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (offer: OffersOutput, product: OfferProducts) => void;
  offer: OffersOutput | null;
  sectionId: string | null;
}

export function OfferModal({
  isOpen,
  onClose,
  onSave,
  offer,
  sectionId,
}: OfferModalProps) {
  const [currentOffer, setCurrentOffer] = useState<OffersOutput | null>(offer);
  const [selectedProduct, setSelectedProduct] = useState<OfferProducts | null>(
    offer?.products?.[0] || null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ProductsOutput[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [initialImage, setInitialImage] = useState<string | undefined>(
    undefined
  );
  const [imageFileId, setImageFileId] = useState<string | null>(
    offer?.fileId || null
  );
  const [isUploading, setIsUploading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    console.log("OfferModal - Oferta recebida:", offer);
    setCurrentOffer(offer);
    setSelectedProduct(offer?.products?.[0] || null);
    setImageFileId(offer?.fileId || null);

    // Definir a imagem inicial
    if (offer?.file && typeof offer.file === "object" && offer.file.url) {
      console.log("OfferModal - Imagem encontrada (objeto):", offer.file.url);
      setInitialImage(offer.file.url);
    } else if (typeof offer?.file === "string") {
      console.log("OfferModal - Imagem encontrada (string):", offer.file);
      setInitialImage(offer.file);
    } else {
      console.log("OfferModal - Nenhuma imagem encontrada");
      setInitialImage(undefined);
    }

    setSearchQuery("");
    setSearchResults([]);
  }, [offer]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      const { data: results } = await productsService.findAll({
        query: query,
        limit: 20,
        expand: "file",
      });
      setSearchResults(results);
      setIsSearching(false);
    }, 300);
  };

  const handleSelectProduct = (product: OfferProducts) => {
    setSelectedProduct(product);
    setSearchResults([]);
    setSearchQuery("");
  };

  const handleImageChange = async (file: File | null) => {
    if (!file) {
      setImageFileId(null);
      return;
    }

    setIsUploading(true);
    const response = await fileService.upload({
      file: file,
      purpose: FilePurpose.OFFERS,
    });
    setImageFileId(response.id);
    setIsUploading(false);
  };

  const calculatePricing = () => {
    if (!selectedProduct || !currentOffer?.unitPrice) {
      return null;
    }

    const originalPrice = selectedProduct.unitPrice;
    const offerPrice = currentOffer.unitPrice;
    const discount = originalPrice - offerPrice;
    const discountPercentage = Math.round((discount / originalPrice) * 100);

    return {
      originalPrice,
      offerPrice,
      discount,
      discountPercentage,
    };
  };

  const pricingInfo = calculatePricing();

  const handleSave = () => {
    if (!currentOffer || !selectedProduct || !sectionId) return;

    const updatedOffer = {
      ...currentOffer,
      fileId: imageFileId || undefined,
    };

    onSave(updatedOffer, selectedProduct);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!currentOffer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] -mx-6 -mt-6 px-6 py-4 rounded-t-lg">
          <DialogTitle className="text-white text-xl">
            Adicionar Produto à Seção
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="offer-name" className="text-sm font-medium">
              Nome da Oferta
            </Label>
            <Input
              id="offer-name"
              placeholder="Digite o nome da oferta"
              className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
              value={currentOffer.name}
              onChange={(e) =>
                setCurrentOffer({ ...currentOffer, name: e.target.value })
              }
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="offer-price" className="text-sm font-medium">
              Preço da Oferta
            </Label>
            <Input
              id="offer-price"
              type="number"
              step="0.01"
              min="0"
              placeholder="0,00"
              className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
              value={currentOffer.unitPrice || ""}
              onChange={(e) =>
                setCurrentOffer({
                  ...currentOffer,
                  unitPrice: parseFloat(e.target.value) || 0,
                })
              }
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="offer-image" className="text-sm font-medium">
              Imagem da Oferta
            </Label>
            <ImageUpload
              initialImage={initialImage}
              onImageChange={handleImageChange}
              disabled={isUploading}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="product-search" className="text-sm font-medium">
              Buscar Produto
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="product-search"
                placeholder="Digite o nome do produto"
                className="pl-10 border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-[#EE7B11] border-t-transparent rounded-full"></div>
                </div>
              )}
              {searchResults?.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                  {searchResults.map((product) => (
                    <div
                      key={product.id}
                      className="p-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b last:border-b-0"
                      onClick={() =>
                        handleSelectProduct({
                          ...product,
                          productId: product.id,
                        })
                      }
                    >
                      <div className="h-12 w-12 relative rounded overflow-hidden bg-gray-100 flex-shrink-0">
                        {product.file?.url ? (
                          <img
                            src={product.file.url || "/placeholder.svg"}
                            alt={product.name}
                            className="w-full h-full object-cover rounded-md"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{product.name}</p>
                        <p className="text-sm text-[#EE7B11] font-semibold">
                          {numberToMoney(product.unitPrice)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedProduct && (
            <div className="border rounded-md p-4 bg-gray-50 mt-2">
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 relative rounded overflow-hidden bg-white">
                  {selectedProduct.file?.url ? (
                    <img
                      src={selectedProduct.file.url || "/placeholder.svg"}
                      alt={selectedProduct.name}
                      className="w-full h-full object-cover rounded-md"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">
                    {selectedProduct.name}
                  </h3>
                  <p className="text-sm text-[#EE7B11] font-semibold">
                    {numberToMoney(selectedProduct.unitPrice)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-500 hover:text-gray-700"
                  onClick={() => setSelectedProduct(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {pricingInfo && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Preço original:</span>
                    <span className="font-medium">
                      {numberToMoney(pricingInfo.originalPrice)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-600">Preço da oferta:</span>
                    <span className="font-semibold text-[#EE7B11]">
                      {numberToMoney(pricingInfo.offerPrice)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-600">Desconto:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-green-600">
                        {numberToMoney(pricingInfo.discount)}
                      </span>
                      <div className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        <Percent className="w-3 h-3" />
                        {pricingInfo.discountPercentage}%
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Switch
              id="offer-visible"
              checked={currentOffer.isVisible}
              onCheckedChange={(checked) =>
                setCurrentOffer({ ...currentOffer, isVisible: checked })
              }
              className="data-[state=checked]:bg-[#EE7B11]"
            />
            <Label htmlFor="offer-visible" className="text-sm font-medium">
              Visível para clientes
            </Label>
          </div>
        </div>
        <DialogFooter className="border-t pt-4 mt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-gray-300"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedProduct}
            className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
          >
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
